import React, { FormEvent, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { BRAND, EMAIL, PHONE_DISPLAY, PHONE_TEL } from '../constants/brand';
import { ROUTES } from '../enum/routes';

const fieldClass =
  'w-full rounded-md border bg-white px-3 py-2.5 text-base text-ink shadow-sm transition focus:outline focus:outline-2 focus:outline-forest focus:outline-offset-2 border-black/15';

type FormState = {
  company: string;
  name: string;
  phone: string;
  email: string;
  address: string;
  service: string;
  appointment: string;
  details: string;
  smsConsent: boolean;
};

const initial: FormState = {
  company: '',
  name: '',
  phone: '',
  email: '',
  address: '',
  service: '',
  appointment: '',
  details: '',
  smsConsent: false,
};

function validEmail(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function validPhone(value: string) {
  return value.replace(/\D/g, '').length >= 10;
}

export default function QuoteForm() {
  const [values, setValues] = useState<FormState>(initial);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [status, setStatus] = useState('');
  const [success, setSuccess] = useState(false);

  const minAppointment = useMemo(() => {
    const now = new Date();
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    return now.toISOString().slice(0, 16);
  }, []);

  const set =
    (key: keyof FormState) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
      const value = e.target.type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value;
      setValues((v) => ({ ...v, [key]: value }));
    };

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    setErrors({});
    setStatus('');

    if (values.company) {
      setSuccess(true);
      return;
    }

    const next: Record<string, string> = {};
    (['name', 'phone', 'email', 'address', 'service', 'appointment', 'details'] as const).forEach((key) => {
      if (!String(values[key] || '').trim()) next[key] = 'This field is required.';
    });
    if (values.email && !validEmail(values.email)) next.email = 'Enter a valid email.';
    if (values.phone && !validPhone(values.phone)) next.phone = 'Enter a valid phone number.';

    if (Object.keys(next).length) {
      setErrors(next);
      setStatus('Please fix the highlighted fields and try again.');
      return;
    }

    const body = [
      `Name: ${values.name}`,
      `Phone: ${values.phone}`,
      `Email: ${values.email}`,
      `Address: ${values.address}`,
      `Service: ${values.service}`,
      `Appointment: ${values.appointment}`,
      `SMS consent: ${values.smsConsent ? 'yes' : 'no'}`,
      '',
      values.details,
    ].join('\n');

    const mailto = `mailto:${EMAIL}?subject=${encodeURIComponent('Free estimate request')}&body=${encodeURIComponent(body)}`;
    try {
      window.location.href = mailto;
    } catch {
      /* ignore */
    }
    setSuccess(true);
  };

  if (success) {
    return (
      <div className="rounded-lg border border-forest/20 bg-white p-8 text-center shadow-sm">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-forest text-white">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>
        <h3 className="h-card mt-5 text-ink">You’re on the list.</h3>
        <p className="mt-3 text-ink/75">Thanks. We’ll reply within 1 business day and help you take the next step.</p>
        <a className="btn-primary mt-6" href={`tel:${PHONE_TEL}`}>
          Or call {PHONE_DISPLAY}
        </a>
      </div>
    );
  }

  const err = (key: string) =>
    errors[key] ? <span className="field-help">{errors[key]}</span> : null;

  return (
    <form className="quote-form relative rounded-lg border border-black/10 bg-white p-6 shadow-sm sm:p-8" noValidate onSubmit={onSubmit}>
      <div className="quote-form__honeypot" aria-hidden="true">
        <label>
          Company
          <input type="text" tabIndex={-1} autoComplete="off" name="company" value={values.company} onChange={set('company')} />
        </label>
      </div>
      <div className="quote-form__grid">
        <label>
          <span>
            Full Name<span className="text-forest"> *</span>
          </span>
          <input type="text" autoComplete="name" className={`${fieldClass}${errors.name ? ' field-error' : ''}`} name="name" required value={values.name} onChange={set('name')} />
          {err('name')}
        </label>
        <label>
          <span>
            Phone<span className="text-forest"> *</span>
          </span>
          <input type="tel" autoComplete="tel" placeholder="(512) 555-0123" className={`${fieldClass}${errors.phone ? ' field-error' : ''}`} name="phone" required value={values.phone} onChange={set('phone')} />
          {err('phone')}
        </label>
        <label>
          <span>
            Email<span className="text-forest"> *</span>
          </span>
          <input type="email" autoComplete="email" className={`${fieldClass}${errors.email ? ' field-error' : ''}`} name="email" required value={values.email} onChange={set('email')} />
          {err('email')}
        </label>
        <label className="quote-form__full">
          <span>
            Project Address<span className="text-forest"> *</span>
          </span>
          <input type="text" autoComplete="street-address" placeholder="123 Main St, Austin, TX 78701" className={`${fieldClass}${errors.address ? ' field-error' : ''}`} name="address" required value={values.address} onChange={set('address')} />
          {err('address')}
        </label>
        <label className="quote-form__full">
          <span>
            What are you planning?<span className="text-forest"> *</span>
          </span>
          <select name="service" className={`${fieldClass}${errors.service ? ' field-error' : ''}`} required value={values.service} onChange={set('service')}>
            <option value="" disabled>
              Select a service
            </option>
            <option value="kitchen">Kitchen Remodeling</option>
            <option value="bathroom">Bathroom Remodeling</option>
            <option value="adu">ADU Construction</option>
            <option value="deck-builders">Deck Builders</option>
            <option value="covered-patios">Covered Patios</option>
            <option value="pergolas">Pergolas</option>
            <option value="outdoor-kitchens">Outdoor Kitchens</option>
            <option value="outdoor-living">Outdoor Living (general)</option>
            <option value="other">Other</option>
          </select>
          {err('service')}
        </label>
        <label className="quote-form__full">
          <span>
            When would you like to talk?<span className="text-forest"> *</span>
          </span>
          <input type="datetime-local" name="appointment" className={`${fieldClass}${errors.appointment ? ' field-error' : ''}`} required min={minAppointment} value={values.appointment} onChange={set('appointment')} />
          <span className="mt-1.5 block text-xs font-normal text-ink/55">Choose a convenient time for your estimate.</span>
          {err('appointment')}
        </label>
        <label className="quote-form__full">
          <span>
            Tell us about your project<span className="text-forest"> *</span>
          </span>
          <textarea
            rows={5}
            placeholder="Tell us what you're hoping to change, your approximate timeline, and anything specific you're envisioning."
            name="details"
            className={`${fieldClass}${errors.details ? ' field-error' : ''}`}
            required
            value={values.details}
            onChange={set('details')}
          />
          {err('details')}
        </label>
      </div>
      <label className="quote-form__consent">
        <input type="checkbox" name="smsConsent" checked={values.smsConsent} onChange={set('smsConsent')} />
        <span>
          Yes, text me about my estimate. I agree to receive text messages (appointment confirmations, reminders, and replies) from {BRAND} at the number I provide. Msg &amp; data rates may apply; message
          frequency varies. Reply STOP to opt out, HELP for help. Consent is not required to book. See our{' '}
          <Link className="underline hover:text-forest" to={ROUTES.TERMS}>
            Terms
          </Link>{' '}
          and{' '}
          <Link className="underline hover:text-forest" to={ROUTES.PRIVACY}>
            Privacy Policy
          </Link>
          .
        </span>
      </label>
      {status ? (
        <p className="mt-4 text-sm text-red-700" role="alert">
          {status}
        </p>
      ) : null}
      <button type="submit" className="btn-primary mt-5 w-full sm:w-auto">
        Book My Free Estimate
      </button>
      <p className="mt-4 text-xs text-ink/60">Free estimate. No obligation. No pressure. We typically respond within 24 hours.</p>
    </form>
  );
}
